"use client";

import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Chip,
  Divider,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import httpClient from "../../utils/httpClient";
import { useNavigate, useParams } from "react-router";
import { Button } from "antd";

const defaultPayment = {
  _id: "",
  receiver_email: "",
  sender_email: "",
  method: "",
  paypalOrderId: "",
  status: "",
  amount: 0,
  currency: "",
  payer: {
    payer_id: "",
    email: "",
    name: "",
    country_code: "",
  },
  capture: {
    amount: { currency_code: "USD", value: 0 },
    seller_receivable_breakdown: {
      gross_amount: { currency_code: "USD", value: 0 },
      paypal_fee: { currency_code: "USD", value: 0 },
      net_amount: { currency_code: "USD", value: 0 },
    },
    id: "",
    status: "",
  },
  payout: {
    sender_batch_header: { sender_batch_id: "" },
    amount: { value: "", currency: "USD" },
    fees: { value: "", currency: "USD" },
    items: {
      payout_item_id: "",
      transaction_id: "",
      transaction_status: "",
      payout_batch_id: "",
      batch_status: "",
    },
  },
  createdAt: "",
  updatedAt: "",
};

const PaymentDetails = () => {
  const { paymentId } = useParams();
  const [payment, setPayment] = useState(defaultPayment);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    httpClient
      .get(`/get-payment/${paymentId}`)
      .then((res) => {
        setPayment(res.data.data || defaultPayment);
        setLoading(false);
      })
      .catch((err) => {
        console.error("get single payment error", err);
        setError("Failed to load payment details.");
        setLoading(false);
      });
  }, [paymentId]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
      case "success":
        return "success";
      case "pending":
      case "created":
        return "warning";
      case "failed":
      case "error":
        return "error";
      default:
        return "info";
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: "600px", margin: "auto", mt: 10 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: "#f9f9f9", minHeight: "100vh", mt: 8 }}>
      <Card sx={{ maxWidth: "1000px", margin: "auto", boxShadow: 4 }}>
        <CardContent>
          <Box sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3
          }}>
            <Typography
              variant="h5"
              gutterBottom
              fontWeight="bold"
              color="primary"
            >
              Payment Details
            </Typography>
            <Button variant="contained"
            onClick={() => navigate(-1)}
            >
                Back
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {/* Basic Info */}
          <Typography variant="h6" color="secondary" gutterBottom>
            Basic Info
          </Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>{payment._id}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>
                  <Chip
                    label={payment.status}
                    color={getStatusColor(payment.status)}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Receiver Email</TableCell>
                <TableCell>{payment.receiver_email}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Sender Email</TableCell>
                <TableCell>{payment.sender_email}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Method</TableCell>
                <TableCell>{payment.method}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>PayPal Order ID</TableCell>
                <TableCell>{payment.paypalOrderId}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Amount</TableCell>
                <TableCell>
                  ${payment.amount} {payment.currency}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Created At</TableCell>
                <TableCell>
                  {payment.createdAt
                    ? new Date(payment.createdAt).toLocaleString()
                    : "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Updated At</TableCell>
                <TableCell>
                  {payment.updatedAt
                    ? new Date(payment.updatedAt).toLocaleString()
                    : "-"}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* Payer Info */}
          <Typography
            variant="h6"
            color="secondary"
            gutterBottom
            sx={{ mt: 3 }}
          >
            Payer Info
          </Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>Payer ID</TableCell>
                <TableCell>{payment.payer?.payer_id || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>{payment.payer?.email || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>{payment.payer?.name || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Country</TableCell>
                <TableCell>{payment.payer?.country_code || "-"}</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* Capture Info */}
          <Typography
            variant="h6"
            color="secondary"
            gutterBottom
            sx={{ mt: 3 }}
          >
            Capture Info
          </Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>Capture Amount</TableCell>
                <TableCell>
                  {payment.capture?.amount?.value}{" "}
                  {payment.capture?.amount?.currency_code}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Gross</TableCell>
                <TableCell>
                  {
                    payment.capture?.seller_receivable_breakdown?.gross_amount
                      ?.value
                  }{" "}
                  {
                    payment.capture?.seller_receivable_breakdown?.gross_amount
                      ?.currency_code
                  }
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>PayPal Fee</TableCell>
                <TableCell>
                  {
                    payment.capture?.seller_receivable_breakdown?.paypal_fee
                      ?.value
                  }{" "}
                  {
                    payment.capture?.seller_receivable_breakdown?.paypal_fee
                      ?.currency_code
                  }
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Net Amount</TableCell>
                <TableCell>
                  {
                    payment.capture?.seller_receivable_breakdown?.net_amount
                      ?.value
                  }{" "}
                  {
                    payment.capture?.seller_receivable_breakdown?.net_amount
                      ?.currency_code
                  }
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Capture ID</TableCell>
                <TableCell>{payment.capture?.id || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>{payment.capture?.status || "-"}</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* Payout Info */}
          <Typography
            variant="h6"
            color="secondary"
            gutterBottom
            sx={{ mt: 3 }}
          >
            Payout Info
          </Typography>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>Batch ID</TableCell>
                <TableCell>
                  {payment.payout?.sender_batch_header?.sender_batch_id || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Amount</TableCell>
                <TableCell>
                  {payment.payout?.amount?.value || "0"}{" "}
                  {payment.payout?.amount?.currency}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Fees</TableCell>
                <TableCell>
                  {payment.payout?.fees?.value || "0"}{" "}
                  {payment.payout?.fees?.currency}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Payout Item ID</TableCell>
                <TableCell>
                  {payment.payout?.items?.payout_item_id || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Transaction ID</TableCell>
                <TableCell>
                  {payment.payout?.items?.transaction_id || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Transaction Status</TableCell>
                <TableCell>
                  {payment.payout?.items?.transaction_status || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Payout Batch ID</TableCell>
                <TableCell>
                  {payment.payout?.items?.payout_batch_id || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Batch Status</TableCell>
                <TableCell>
                  {payment.payout?.items?.batch_status || "-"}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentDetails;
